<template>
  <div class="internal-chat-container h-full flex flex-col">
    <!-- Header -->
    <div class="internal-chat-header">
      <div class="flex items-center gap-3">
        <div class="support-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
        </div>
        <div>
          <h2 class="header-title">Feedback & Support</h2>
          <p class="header-subtitle">Talk to our team</p>
        </div>
      </div>
    </div>

    <!-- Messages Area -->
    <div ref="messagesContainer" class="messages-area px-12" @scroll="handleScroll">
      <div v-if="isLoadingMessages" class="loading-container">
        <div class="loading-spinner"></div>
        <p>Loading messages...</p>
      </div>
      
      <div v-else-if="messages.length === 0" class="empty-state">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
        <p>No messages yet</p>
        <p class="empty-subtitle">Start a conversation with our support team</p>
      </div>

      <div v-else>
        <!-- Loading more indicator -->
        <div v-if="isLoadingMore" class="loading-more-container">
          <div class="spinner-small"></div>
          <span>Loading more messages...</span>
        </div>

        <div class="messages-list">
          <template v-for="item in messages" :key="item._id">
            <!-- Failed message -->
            <MessageNotSent
              v-if="item.failed"
              :message-text="item.msg"
              :sender-name="getSenderName(item)"
              :timestamp="item.ts"
            />
            <!-- Normal message -->
            <MessageUnit
              v-else
              :message-data="item"
              :rc_user_id="currentUserId"
              :message-type="item.t"
            />
          </template>
        </div>
      </div>
    </div>

    <!-- Input Area (matches patient chat style without quick actions) -->
    <div class="w-full px-12 pb-8 pt-6">
      <div class="flex flex-col message-outer rounded-[5px]">
        <div 
          class="bg-white px-4 py-3 rounded-[5px] flex flex-col gap-3 min-h-[100px]"
        >
          <textarea
            v-model="newMessage"
            placeholder="Type here to reply ..."
            class="flex-1 border-none outline-none text-[16px] text-[#333333] tracking-[0.15px] leading-[24px] h-[60px] resize-none overflow-y-auto"
            @keydown.enter.prevent="sendMessage"
          ></textarea>
          
          <div class="flex gap-2 items-center justify-end">
            <ResponsiveButton
              :disabled="!newMessage.trim() || isSending"
              button_type="filled"
              built_in_theme="dark"
              size="small"
              :button_border_radius="'15px'"
              :display_name="isSending ? 'Sending...' : 'Send'"
              @click-button="sendMessage"
            >
              <template #prefix>
                <img v-if="!isSending" src="@/assets/MyPatient/arrow_upward.svg" alt="Send" />
                <div v-else class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              </template>
            </ResponsiveButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RocketChatService from '@/services/rocketChatService';
import wsService from '@/services/websocketService.js';
import MessageUnit from '@/views/MyPatient-demo/SupportChat/MessageUnit.vue';
import MessageNotSent from '@/views/MyPatient-demo/MessageNotSent.vue';
import { markAsReadApi } from '@/api/ehrRooms';

export default {
  name: 'InternalChat',
  components: {
    MessageUnit,
    MessageNotSent,
  },
  props: {
    supportRoom: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      newMessage: '',
      isLoadingMessages: false,
      isLoadingMore: false,
      isSending: false,
      messagesPerPage: 50
    };
  },
  computed: {
    roomId() {
      return this.supportRoom?.room_id || null;
    },
    currentUserId() {
      return this.$store.state.login?.rc_user_id || '';
    },
    token() {
      return this.$store.state.login?.token || '';
    },
    messages() {
      const msgs = this.$store.getters['myPatients/message/getRoomMessages'](this.roomId) || [];
      console.log(`📬 InternalChat: Retrieved ${msgs.length} messages from store for room:`, this.roomId);
      return msgs;
    },
    totalMessages() {
      return this.$store.getters['myPatients/message/getTotalMessageCount'](this.roomId);
    },
    currentMessageCount() {
      return this.messages.length;
    },
    hasMoreMessages() {
      return this.$store.getters['myPatients/message/hasMoreMessages'](this.roomId);
    }
  },
  watch: {
    roomId: {
      handler(newRoomId) {
        if (newRoomId) {
          // Always refetch messages when support room is selected
          this.loadMessages();
          this.markRoomAsRead();
        }
      },
      immediate: true
    },
    messages: {
      handler(newMessages, oldMessages) {
        // Detect new messages arriving (not from initial load)
        if (oldMessages && newMessages.length > oldMessages.length) {
          // New message arrived, increment total
          const newCount = newMessages.length - oldMessages.length;
          for (let i = 0; i < newCount; i++) {
            this.$store.dispatch('myPatients/message/incrementRoomTotal', this.roomId);
          }
        }
        // Auto-scroll to bottom on any message change
        this.scrollToBottom();
      },
      deep: true
    }
  },
  beforeUnmount() {
    // Parent index.vue handles WebSocket subscription/unsubscription
  },
  methods: {
    async loadMessages() {
      if (!this.roomId) return;

      console.log('🔄 InternalChat: Loading messages for room:', this.roomId);
      this.isLoadingMessages = true;
      const headers = {
        'Authorization': `Bearer ${this.token}`
      };

      try {
        this.$store.dispatch('myPatients/message/initializeRoom', {
          roomId: this.roomId,
          messages: []
        });

        const response = await RocketChatService.get(`/messages?roomId=${this.roomId}&count=${this.messagesPerPage}&offset=0`, { headers });
        if (response.data && response.data.messages) {
          const messages = response.data.messages.reverse();
          console.log(`✅ InternalChat: Loaded ${messages.length} messages for room ${this.roomId}`);

          this.$store.dispatch('myPatients/message/setRoomMessages', {
            roomId: this.roomId,
            messages: messages
          });

          if (response.data.total !== undefined) {
            this.$store.dispatch('myPatients/message/setRoomTotal', {
              roomId: this.roomId,
              total: response.data.total
            });
          }

          this.$nextTick(() => {
            this.scrollToBottom();
          });
        }
      } catch (error) {
        console.error('❌ Failed to load support messages:', error);
      } finally {
        this.isLoadingMessages = false;
      }
    },

    async loadMoreMessages() {
      if (!this.roomId || this.isLoadingMore || !this.hasMoreMessages) return;

      this.isLoadingMore = true;
      const headers = {
        'Authorization': `Bearer ${this.token}`
      };

      const offset = this.currentMessageCount;

      try {
        const response = await RocketChatService.get(
          `/messages?roomId=${this.roomId}&count=${this.messagesPerPage}&offset=${offset}`,
          { headers }
        );

        if (response.data && response.data.messages) {
          const newMessages = response.data.messages.reverse();
          const updatedMessages = [...newMessages, ...this.messages];
          
          this.$store.dispatch('myPatients/message/setRoomMessages', {
            roomId: this.roomId,
            messages: updatedMessages
          });

          if (response.data.total !== undefined) {
            this.$store.dispatch('myPatients/message/setRoomTotal', {
              roomId: this.roomId,
              total: response.data.total
            });
          }
        }
      } catch (error) {
        console.error('Failed to load more messages:', error);
      } finally {
        this.isLoadingMore = false;
      }
    },

    handleScroll(event) {
      const container = event.target;
      const scrollTop = container.scrollTop;

      if (scrollTop < 50 && this.hasMoreMessages && !this.isLoadingMore && !this.isLoadingMessages) {
        const oldScrollHeight = container.scrollHeight;

        this.loadMoreMessages().then(() => {
          this.$nextTick(() => {
            const newScrollHeight = container.scrollHeight;
            container.scrollTop = scrollTop + (newScrollHeight - oldScrollHeight);
          });
        });
      }
    },

    async sendMessage() {
      if (this.isSending || !this.newMessage.trim()) return;
      
      this.isSending = true;
      const messageText = this.newMessage.trim();
      
      const messageBody = {
        "message": {
          "rid": this.roomId,
          "msg": messageText,
          "alias": "",
          "emoji": "",
          "avatar": "",
          "attachments": []
        }
      };
      
      try {
        const response = await RocketChatService.post('/messages', messageBody);
        
        if (response.status === 201 && response.data && response.data.success) {
          const responseMessage = response.data.message;
          const messageId = responseMessage._id;
          const roomId = responseMessage.rid;
          
          const existingSentMessage = this.$store.getters['myPatients/message/getSentMessage'](roomId, messageId);
          
          if (!existingSentMessage) {
            this.$store.dispatch('myPatients/message/addSentMessage', {
              roomId: roomId,
              messageId: messageId,
              message: responseMessage
            });

            this.$store.dispatch('myPatients/message/addMessage', {
              roomId: roomId,
              message: responseMessage
            });

            this.$store.dispatch('myPatients/message/incrementRoomTotal', this.roomId);
          }

          this.newMessage = '';
          this.scrollToBottom();
        }
      } catch (error) {
        console.error('❌ Failed to send message:', error);
        
        const failedMessage = {
          _id: `failed-${Date.now()}`,
          rid: this.roomId,
          msg: messageText,
          ts: new Date().toISOString(),
          u: {
            _id: this.currentUserId,
            username: this.$store.state.login.userInfo?.username || 'user'
          },
          t: undefined,
          failed: true,
          failedAt: new Date().toISOString()
        };
        
        this.$store.dispatch('myPatients/message/addMessage', {
          roomId: this.roomId,
          message: failedMessage
        });
        
        this.newMessage = '';
        this.scrollToBottom();
      } finally {
        this.isSending = false;
      }
    },

    async markRoomAsRead() {
      if (!this.roomId) return;
      
      try {
        await markAsReadApi({ rid: this.roomId });
        this.$store.dispatch('myPatients/rooms/updateRoomLastSeen', {
          roomId: this.roomId,
          lastSeenData: { unread: 0, ls: new Date().toISOString() }
        });
      } catch (error) {
        console.error('Error marking as read:', error);
      }
    },

    scrollToBottom() {
      // Use nextTick to ensure DOM is updated before scrolling
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (container) {
          // Smooth scroll to bottom for better UX (same as patient chat)
          container.scrollTo({
            top: container.scrollHeight,
            behavior: 'smooth'
          });

          // Fallback for browsers that don't support smooth scroll
          if (container.scrollTop === 0) {
            container.scrollTop = container.scrollHeight;
          }
        }
      });
    },

    getSenderName(message) {
      return message.u?.name || message.u?.username || 'Unknown';
    }
  }
};
</script>

<style scoped>
.internal-chat-container {
  background: white;
  border-right: 1px solid #e5e7eb;
}

/* Header */
.internal-chat-header {
  padding: 24px 32px;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
}

.support-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.header-title {
  font-family: Inter, sans-serif;
  font-size: 20px;
  font-weight: 600;
  color: #0c4a6e;
  margin: 0;
}

.header-subtitle {
  font-family: Inter, sans-serif;
  font-size: 14px;
  color: #0369a1;
  margin: 2px 0 0 0;
}

/* Messages Area */
.messages-area {
  flex: 1;
  overflow-y: auto;
  padding-top: 24px;
  padding-bottom: 24px;
}

.loading-container,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #6b7280;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e5e7eb;
  border-top-color: #0ea5e9;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-state svg {
  color: #9ca3af;
  margin-bottom: 16px;
}

.empty-state p {
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #6b7280;
  margin: 8px 0;
}

.empty-subtitle {
  font-size: 14px !important;
  font-weight: 400 !important;
  color: #9ca3af !important;
}

.loading-more-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  color: #6b7280;
  font-size: 14px;
}

.spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid #e5e7eb;
  border-top-color: #0ea5e9;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.messages-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Message outer container */
.message-outer {
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}
</style>
